// docs/VISUAL-DESIGN.md §8b: the backdrop is a surface, not a colour. The Astra constellation
// tile and the grain sprite, generated ONCE at boot from the same table as
// ios/Sources/Design/WallpaperPattern.swift and android/.../WallpaperPattern.kt (change a
// coordinate there, change it here), and handed to CSS as custom properties. The blooms and the
// lift-to-shade are plain CSS gradients in app.css.

const SIZE = 360;

const stars: Array<[number, number, number]> = [
  [30, 48, 2.2], [96, 20, 1.6], [150, 96, 2.6], [210, 40, 1.8], [300, 88, 2.2], [64, 150, 1.8],
  [180, 200, 2.4], [268, 168, 1.6], [340, 236, 2.2], [40, 268, 2.6], [128, 300, 1.8], [232, 320, 2.2],
  [312, 296, 1.6], [86, 344, 2.0],
  // A star at the centre of every orbit — without it the orbits read as plain bubbles.
  [96, 96, 3.0], [268, 60, 2.2], [52, 208, 2.4], [300, 220, 3.0], [176, 296, 2.6],
];
const orbits: Array<[number, number, number, number]> = [
  [96, 96, 26, 1.4], [268, 60, 16, 1.2], [52, 208, 18, 1.2], [300, 220, 28, 1.4], [176, 296, 20, 1.2],
];
// Degrees CLOCKWISE from 3 o'clock in a y-down space (Android's drawArc); SVG is y-down too.
const tails: Array<[number, number, number, number, number, number]> = [
  [96, 96, 40, -34, 96, 1.4], [300, 220, 43, 146, 88, 1.4], [176, 296, 33, 16, 74, 1.2],
];

function arc(cx: number, cy: number, r: number, start: number, sweep: number): string {
  const a0 = (start * Math.PI) / 180;
  const a1 = ((start + sweep) * Math.PI) / 180;
  const x0 = cx + r * Math.cos(a0);
  const y0 = cy + r * Math.sin(a0);
  const x1 = cx + r * Math.cos(a1);
  const y1 = cy + r * Math.sin(a1);
  return `M${x0.toFixed(2)} ${y0.toFixed(2)}A${r} ${r} 0 ${sweep > 180 ? 1 : 0} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}

/** The tile, seamless BY CONSTRUCTION: every element drawn nine times, shifted by ±tile. */
function patternSvg(ink: string): string {
  const parts: string[] = [];
  for (const dx of [-SIZE, 0, SIZE]) {
    for (const dy of [-SIZE, 0, SIZE]) {
      for (const [x, y, r] of stars) parts.push(`<circle cx="${x + dx}" cy="${y + dy}" r="${r}" fill="${ink}"/>`);
      for (const [x, y, r, w] of orbits) parts.push(`<circle cx="${x + dx}" cy="${y + dy}" r="${r}" fill="none" stroke="${ink}" stroke-width="${w}"/>`);
      for (const [x, y, r, s, sw, w] of tails)
        parts.push(`<path d="${arc(x + dx, y + dy, r, s, sw)}" fill="none" stroke="${ink}" stroke-width="${w}" stroke-linecap="round"/>`);
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">${parts.join('')}</svg>`;
}

function svgUrl(svg: string): string {
  return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`;
}

/**
 * The 96 px grain sprite with the SAME fixed-seed LCG as AmbientSurface.swift, so the texture is
 * identical in every screenshot (and between the Mac and the browser beside it).
 */
function grainUrl(): string | null {
  try {
    const size = 96;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const image = ctx.createImageData(size, size);
    let state = 20_260_905n;
    const mask = (1n << 64n) - 1n;
    const next = () => {
      state = (state * 6_364_136_223_846_793_005n + 1_442_695_040_888_963_407n) & mask;
      return Number((state >> 33n) & 0xffffffffn);
    };
    for (let i = 0; i < size * size; i++) {
      const alpha = next() % 160;
      const lum = next() % 2 === 0 ? 255 : 0;
      image.data[i * 4] = lum;
      image.data[i * 4 + 1] = lum;
      image.data[i * 4 + 2] = lum;
      image.data[i * 4 + 3] = alpha;
    }
    ctx.putImageData(image, 0, 0);
    return `url("${canvas.toDataURL('image/png')}")`;
  } catch {
    return null;
  }
}

export function installWallpaper(): void {
  const root = document.documentElement.style;
  // Light constellation on dark, the brand's own violet (#5B21B6) on light — two tiles, because
  // a single template tinted at paint time cannot be done with a CSS background image.
  root.setProperty('--pattern-on-dark', svgUrl(patternSvg('white')));
  root.setProperty('--pattern-on-light', svgUrl(patternSvg('rgb(91,33,182)')));
  const grain = grainUrl();
  if (grain) root.setProperty('--grain', grain);
}
